/**
 * RankingCard — numbered list of sites by overall weighted score, plus the
 * "What if?" entry point to SensitivityAnalysis. Split out of the original
 * VisualizationPanel so it can live in its own grid cell in the redesigned
 * two-row Scoring layout.
 */

import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Trophy, Sliders } from 'lucide-react'

import RankingList from './RankingList'
import SensitivityAnalysis from './SensitivityAnalysis'

interface SiteSummary {
	siteId: string
	siteName: string
	overallScore: number | null
	criteria: Array<{ criterionId: string; name: string; weight: number; avgScore: number | null }>
}

interface RankingCardProps {
	evalId: string
	/**
	 * When set, show this many sites and scroll the rest. Omitted by the live
	 * Scoring tab, which still renders the full list at its natural height.
	 */
	maxVisible?: number
}

const RankingCard = ({ evalId, maxVisible }: RankingCardProps) => {
	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])
	const sites: SiteSummary[] = summary?.sites ?? []
	const [sensitivityOpen, setSensitivityOpen] = useState(false)

	const ranked = useMemo(
		() =>
			sites
				.filter(s => s.overallScore != null)
				.slice()
				.sort((a, b) => (b.overallScore ?? 0) - (a.overallScore ?? 0)),
		[sites]
	)

	const hasAnyScores = ranked.length > 0

	return (
		<>
			<Card>
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between gap-2">
						<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
							<Trophy className="w-4 h-4" />
							Ranking
						</h4>
						{hasAnyScores && (
							<Button
								variant="primary"
								isOutline
								onClick={() => setSensitivityOpen(true)}
								title="Sensitivity analysis (what-if weights)"
								aria-label="Open sensitivity analysis"
								className="gap-1 px-2 py-1 text-xs h-7"
							>
								<Sliders className="w-3.5 h-3.5" />
								What if?
							</Button>
						)}
					</div>
				</CardHeader>
				<CardContent>
					{!hasAnyScores ? (
						<p className="text-xs text-gray-400 text-center py-4">Score sites in the centre matrix to see rankings.</p>
					) : (
						<RankingList
							entries={ranked.map(site => ({ id: site.siteId, name: site.siteName, score: site.overallScore }))}
							maxVisible={maxVisible}
						/>
					)}
				</CardContent>
			</Card>

			<SensitivityAnalysis open={sensitivityOpen} onClose={() => setSensitivityOpen(false)} evalId={evalId} />
		</>
	)
}

export default RankingCard
